import { isNullOrUndefinedOrEmpty, delay } from 'rewire-common';
import { observable } from 'rewire-core';
import { notifications } from '../ui/Notifications';
import { ISubscriptionRecord } from '../DataTypes';
import PagingModel from '../settings/PagingModel';

export type EntitySort<T> = (e1: T, e2: T) => number;
export type SubscriptionCallbackFn<T> = (sr: ISubscriptionRecord<T>) => void;

interface IEntitySubscriptionModelState<T> {
  entities: T[];
}

export abstract class EntitySubscriptionModel<T, TState = unknown> {
  protected _subscription: any;
  protected _updating: boolean;
  protected _state: IEntitySubscriptionModelState<T> & TState;
  protected _returnSchema: string;
  protected _filter?: Record<string, unknown>;
  protected _sort?: EntitySort<T>;
  protected _pagingModel?: PagingModel;
  protected _usingExternalEntitiesContainer: boolean;
  protected _subscriptionCallback?: SubscriptionCallbackFn<T>;

  constructor(options?: {
    returnSchema?: string;
    filter?: Record<string, unknown>;
    sort?: EntitySort<T>;
    pagingModel?: PagingModel;
    externalEntitiesContainer?: T[];
    subscriptionCallback?: SubscriptionCallbackFn<T>;
  }) {
    this._subscription = undefined;
    this._updating = false;
    this._returnSchema = options?.returnSchema || 'id';
    this._filter = options?.filter;
    this._sort = options?.sort;
    this._pagingModel = options?.pagingModel;
    this._subscriptionCallback = options?.subscriptionCallback;
    this._usingExternalEntitiesContainer = !!options?.externalEntitiesContainer;
    this._state = observable({
      entities: options?.externalEntitiesContainer ?? [],
    }) as IEntitySubscriptionModelState<T> & TState;
  }

  set returnSchema(value: string) {
    this._returnSchema = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get returnSchema(): string {
    return this._returnSchema;
  }

  set filter(value: Record<string, unknown> | undefined) {
    this._filter = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get filter(): Record<string, unknown> | undefined {
    return this._filter;
  }

  set sort(value: EntitySort<T> | undefined) {
    this._sort = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get sort(): EntitySort<T> | undefined {
    return this._sort;
  }

  set pagingModel(value: PagingModel | undefined) {
    this.pagingModel = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  get pagingModel(): PagingModel | undefined {
    return this._pagingModel;
  }

  get usingExternalEntitesContainer(): boolean {
    return this._usingExternalEntitiesContainer;
  }

  set entities(value: T[]) {
    this._state.entities.set(value ?? []);
  }

  get entities(): T[] {
    return this._state.entities;
  }

  get entityCount(): number {
    return this.entities.length;
  }

  protected async getEntities(): Promise<T[]> {
    const entities = await this._getEntities(this.returnSchema, this.filter, this.pagingModel);
    this.sort && entities.sort(this.sort);
    return entities;
  }

  protected abstract _getEntities(returnSchema: string, filter?: Record<string, unknown>, pagingModel?: PagingModel): Promise<T[]>;

  protected abstract subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any;

  reset() {
    this.stopUpdate();
    this.entities = [];
  }

  protected entityEquals(e1: any, e2: any): boolean {
    // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
    return !isNullOrUndefinedOrEmpty(e2['id']) && !isNullOrUndefinedOrEmpty(e1['id']) ? e2['id'] == e1['id'] : e2 == e1;
  }

  protected abstract mergeAccessor(entityRecord: ISubscriptionRecord<T>): T;

  _merging: boolean = false;
  _toProcess: ISubscriptionRecord<T>[] = [];

  protected getSubscriptionCallback() {
    return async (entityRecord: ISubscriptionRecord<T>) => {
      if (isNullOrUndefinedOrEmpty(entityRecord)) return;
      await this.merge(entityRecord);
      this._subscriptionCallback?.(entityRecord);
    };
  }

  setSubscriptionCallback(value: SubscriptionCallbackFn<T>) {
    this._subscriptionCallback = value;
    if (this._subscription) {
      this.refreshUpdate();
    }
  }

  protected async merge(entityRecord: ISubscriptionRecord<T>) {
    this._toProcess.push(entityRecord);
    if (this._merging) return;
    this._merging = true;
    await delay(250);
    const toMerge = this._toProcess;
    this._toProcess = [];
    this._merging = false;
    if (toMerge.length === 0) return;

    const entities: T[] = [];
    const merged: T[] = [];
    for (const e of this.entities) {
      const merge = toMerge.find((e2Record) => this.entityEquals(this.mergeAccessor(e2Record), e));
      if (!merge) {
        entities.push(e);
        continue;
      }
      const entity = this.mergeAccessor(merge);
      merged.push(entity);
      if (merge.operation === 'deleted') continue;
      entities.push(entity);
    }
    for (const merge of toMerge) {
      const mergeEntity = this.mergeAccessor(merge);
      if (merge.operation === 'deleted') continue;
      if (merged.some((i) => this.entityEquals(i, mergeEntity))) continue;
      entities.push(mergeEntity);
    }
    this.sort && entities.sort(this.sort);
    this.entities = entities;
  }

  async startUpdate(): Promise<boolean> {
    if (this._updating || this._subscription) return false;
    if (!this.filter) {
      notifications.warning('cannot subscribe without a filter!');
      return false;
    }
    this._updating = true;
    if (!this.pagingModel || !this.usingExternalEntitesContainer) {
      // load intitial entities, as this load isn't being handled externally
      this.entities = await this.getEntities();
    }
    const callback = this.getSubscriptionCallback();
    this._subscription = this.subscribe(this.returnSchema, this.filter, callback);
    this._updating = false;
    return true;
  }

  stopUpdate() {
    if (!this._subscription) {
      return false;
    }
    this._subscription?.unsubscribe();
    this._subscription = undefined;
    return true;
  }

  async refreshUpdate(): Promise<boolean> {
    this.stopUpdate();
    return await this.startUpdate();
  }
}

export default abstract class EntitySubscriptionService<T, TSubscriptionModel extends EntitySubscriptionModel<T>> {
  subscriptionModels: EntitySubscriptionModel<T>[];

  constructor() {
    this.subscriptionModels = [];
  }

  createSubscriptionModel(options?: {
    returnSchema?: string;
    filter?: any;
    sort?: EntitySort<T>;
    pagingModel?: PagingModel;
    externalEntitiesContainer?: T[];
    subscriptionCallback?: SubscriptionCallbackFn<T>;
  }): TSubscriptionModel {
    const newSubscriptionModel = this._createSubscriptionModel(options);

    this.subscriptionModels.push(newSubscriptionModel);
    return newSubscriptionModel;
  }

  protected abstract _createSubscriptionModel(options?: {
    returnSchema?: string;
    filter?: Record<string, unknown>;
    sort?: EntitySort<T>;
    pagingModel?: PagingModel;
    externalEntitiesContainer?: T[];
    subscriptionCallback?: SubscriptionCallbackFn<T>;
  }): TSubscriptionModel;

  stopUpdates() {
    for (const m of this.subscriptionModels) {
      m.stopUpdate();
    }
  }
}
